import React, { FC, useEffect, useState } from 'react'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { useAuth } from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

type AttachmentItem = {
  id: number
  fileName: string
  fileType?: string
  fileSize?: number
  viewUrl: string
  downloadUrl: string
}

type Props = {
  visible: boolean
  title: string
  items: AttachmentItem[]
  allowRemove?: boolean
  onClose: () => void
  onRemove?: (attachmentId: number) => void
  onViewAttachment?: (item: AttachmentItem) => void
}

const formatBytes = (n?: number) => {
  const v = Number(n ?? 0)
  if (!Number.isFinite(v) || v <= 0) return '-'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = v
  let i = 0
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i++
  }
  const digits = i === 0 ? 0 : 1
  return `${size.toFixed(digits)} ${units[i]}`
}

const CrewComplaintAttachmentsModal: FC<Props> = ({ visible, title, items, allowRemove, onClose, onRemove, onViewAttachment }) => {
  const { auth } = useAuth()
  const token = auth?.auth?.jwt

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  useEffect(() => {
    if (!visible) return
    setCurrentPage(1)
    setRowsPerPage(10)
    setDownloadingId(null)
  }, [visible, items.length])

  if (!visible) return null

  const total = items.length
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage))
  const start = (currentPage - 1) * rowsPerPage
  const pageItems = items.slice(start, start + rowsPerPage)

  const handleDownload = async (item: AttachmentItem) => {
    try {
      setDownloadingId(item.id)
      const res = await fetch(item.downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })

      if (!res.ok) {
        throw new Error(`Download failed: ${res.status}`)
      }

      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = item.fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    } catch (error: any) {
      console.error(error)
      showErrorToast(error, 'Download failed')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleView = async (item: AttachmentItem) => {
    if (onViewAttachment) {
      onViewAttachment(item)
      return
    }

    try {
      const res = await fetch(item.viewUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })

      if (!res.ok) {
        throw new Error(`View failed: ${res.status}`)
      }

      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (error: any) {
      console.error(error)
      showErrorToast(error, 'View failed')
    }
  }

  return (
    <div style={WRAP_STYLE} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='bg-white rounded shadow-lg d-flex flex-column' style={{ width: '100%', maxWidth: 850, maxHeight: '90vh' }}>
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <h5 className='modal-title'>{title}</h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>
        <div className='p-5 text-dark flex-grow-1' style={{ overflowY: 'auto' }}>
          {total === 0 ? (
            <div className='text-muted text-center py-4'>No attachments found.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-sm align-middle mb-0'>
                <thead>
                  <tr className='bg-light-primary'>
                    <th style={{ width: 70 }}>Sr/No</th>
                    <th>File Name</th>
                    <th style={{ width: 140 }}>Type</th>
                    <th style={{ width: 120 }}>Size</th>
                    <th className='text-end pe-6' style={{ width: 220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((att, idx) => (
                    <tr key={att.id}>
                      <td>{start + idx + 1}</td>
                      <td>
                        <div className='text-truncate' style={{ maxWidth: '360px' }} title={att.fileName}>
                          {att.fileName}
                        </div>
                      </td>
                      <td>{att.fileType || '-'}</td>
                      <td>{formatBytes(att.fileSize)}</td>
                      <td>
                        <div className='d-flex justify-content-end align-items-center gap-2 flex-nowrap'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary'
                            onClick={() => handleView(att)}
                          >
                            View
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-light-info'
                            onClick={() => handleDownload(att)}
                            disabled={downloadingId === att.id}
                          >
                            {downloadingId === att.id ? 'Downloading...' : 'Download'}
                          </button>
                          {allowRemove && onRemove && (
                            <button className='btn btn-sm btn-light-danger' onClick={() => onRemove(att.id)}>Remove</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className='d-flex justify-content-between align-items-center border-top px-5 py-4'>
          <div className='text-muted small'>
            Page {currentPage} of {totalPages}
          </div>
          <div className='btn-group'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <SearchableSelect
              className='form-select form-select-sm w-auto'
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setCurrentPage(1)
              }}
            >
              {[5, 10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </SearchableSelect>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CrewComplaintAttachmentsModal
